/**
 * Trip Local Notifications — ShiftQuote Auto-Track.
 *
 * Fires local (on-device) notifications when:
 *   - A trip starts being tracked
 *   - A trip ends and is saved
 *
 * Uses @capacitor/local-notifications when available (native), otherwise
 * falls back to the Web Notifications API for PWA testing.
 *
 * Rules:
 *   - Only ONE start notification per trip (not repeated while driving)
 *   - Only ONE end notification per trip
 *   - Notifications are local, not server-pushed
 */
import { Capacitor } from "@capacitor/core";

// Notification channel for Android (grouped under Mileage)
const CHANNEL_ID = "shiftquote_mileage_tracker";
const CHANNEL_NAME = "Mileage Tracker";

let _initialized = false;

/**
 * Lazy-load LocalNotifications plugin (native only).
 * Uses dynamic import with webpackIgnore so the web build doesn't fail
 * if the package has resolution issues in a non-native environment.
 */
async function getLocalNotifications() {
  try {
    const mod = await import(
      /* webpackIgnore: true */ "@capacitor/local-notifications"
    );
    return mod.LocalNotifications;
  } catch (e) {
    console.warn("[tripNotifications] LocalNotifications unavailable:", e.message);
    return null;
  }
}

/**
 * Initialize notification channel (Android). Safe to call multiple times.
 */
async function ensureChannel() {
  if (_initialized) return;
  _initialized = true;

  if (!Capacitor.isNativePlatform()) return;

  try {
    const LocalNotifications = await getLocalNotifications();
    if (!LocalNotifications) return;

    // Check permission
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display === "prompt" || perm.display === "prompt-with-rationale") {
      await LocalNotifications.requestPermissions();
    }

    // Create Android notification channel
    if (Capacitor.getPlatform() === "android") {
      await LocalNotifications.createChannel({
        id: CHANNEL_ID,
        name: CHANNEL_NAME,
        description: "Notifications for automatic mileage trip tracking",
        importance: 3, // DEFAULT
        visibility: 1, // PUBLIC
        vibration: true,
      });
    }
  } catch (e) {
    console.warn("[tripNotifications] Channel setup failed:", e);
  }
}

/**
 * Fire the "trip started" local notification.
 * Called ONCE when motion detection confirms a real trip has begun
 * (30s continuous movement, speed > 5 mph).
 */
export async function notifyTripStarted() {
  await ensureChannel();

  const title = "ShiftQuote is tracking your trip";
  const body = "Automatic mileage logging has started. Drive safe!";

  if (!Capacitor.isNativePlatform()) {
    // Web fallback for dev/testing
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body });
    }
    return;
  }

  try {
    const LocalNotifications = await getLocalNotifications();
    if (!LocalNotifications) return;

    await LocalNotifications.schedule({
      notifications: [
        {
          id: generateNotificationId(),
          title,
          body,
          channelId: CHANNEL_ID,
          schedule: { at: new Date(Date.now() + 100) }, // Immediate
          smallIcon: "ic_stat_mileage",
          largeIcon: "ic_launcher",
          sound: "default",
        },
      ],
    });
  } catch (e) {
    console.warn("[tripNotifications] Failed to send trip-start notification:", e);
  }
}

/**
 * Fire the "trip ended" local notification.
 * Called ONCE when the trip is finalized and saved (2 min stationary, >= 0.5 mi).
 */
export async function notifyTripEnded() {
  await ensureChannel();

  const title = "Trip logged";
  const body = "Your trip has been saved. Parking location saved. Tap to review your mileage.";

  if (!Capacitor.isNativePlatform()) {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body });
    }
    return;
  }

  try {
    const LocalNotifications = await getLocalNotifications();
    if (!LocalNotifications) return;

    await LocalNotifications.schedule({
      notifications: [
        {
          id: generateNotificationId(),
          title,
          body,
          channelId: CHANNEL_ID,
          schedule: { at: new Date(Date.now() + 100) }, // Immediate
          smallIcon: "ic_stat_mileage",
          largeIcon: "ic_launcher",
          sound: "default",
          // Tapping opens the mileage page
          extra: { route: "/mileage" },
        },
      ],
    });
  } catch (e) {
    console.warn("[tripNotifications] Failed to send trip-end notification:", e);
  }
}

/**
 * Generate a pseudo-unique notification ID (int32).
 * LocalNotifications requires a numeric id to avoid collisions.
 */
function generateNotificationId() {
  return Math.floor(Math.random() * 2147483647);
}

export { ensureChannel };
